import { CurrencyType, Nullable } from "..";

export type DataroomDateRange = { from: string | null; to: string | null };

export interface DataRooms {
  connections: ConnectionPreview[];
  connection: Connection | null;
  connectMessage: {
    message: string;
    code: string;
    countdown: string;
  } | null;
  platforms?: Platform[];
}

export interface IConnectionProcess {
  id: number;
  full_name: string;
  batch_hash: string;
  user_id: number;
  operation: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface ConnectionPreview<UpdateDateType = Date> {
  id: number;
  type: string;
  type_title: string;
  username: string;
  subdomain: string | null;
  updated_at: UpdateDateType | null;
  last_connected: string | null;
  last_sync: string | null;
  last_index: string | null;
  last_download: string | null;
  last_login_error: null | "2FA_REQUIRED";
  last_login_error_timestamp: null | number;
  last_login_timestamp: null | number;
  logo: string | null;
  is_logged: boolean;
  status: null | string;
  is_login_valid: boolean | null;
  mfa: boolean;
  enable_auto_sync?: boolean;
  files: {
    indexed: number;
    downloaded: number;
    updated_data: Record<string, number> | null;
  };
}

export interface Connection extends ConnectionPreview {
  relations: Relation[];
}

export interface Relation {
  supplier: Supplier;
  consumer: Nullable<Consumer>;
  files?: {
    indexed: number;
    downloaded: number;
  };
  processes?: IConnectionProcess[];
  last_sync?: string | null;
  last_index?: string | null;
}

export interface Supplier {
  id: string | number;
  name: string;
}

export interface Consumer {
  id: number;
  dealId: number;
}

// export type DataRoomItem = {
//   source_deal:
// }

export interface Platform {
  id: string;
  name: string;
  logo: string;
  extra: { [key: string]: string | boolean | null };
}

export interface IFileDataroom {
  id: string | number;
  name: string;
  created_at: string;
  updated_at: string | null;
  uploaded_at: string | null;
  effective_at: string | null;
  indexed_at: string | null;
  tags: Tags;
  downloaded_at: string | null;
  last_connected: string | null;
  dataroom: string;
  altius_type?: string;
}

export interface FilterOptions {
  accountNameValues: string[];
  docTypeValues: string[];
  dealNameValues: string[];
  altiusDealNameValues: string[];
}

export interface PagedDataroomFiles {
  files: IFileDataroom[];
  total_files: number;
  total_pages: number;
  page_number: number;
  page_size: number;
}

export interface Tags {
  accountName: string;
  fundName: string;
  docType: string;
  fundId: string;
  fetcherId: number;
  dealName?: string;
  dealId?: number;
  dataroom: string;
  downloaded_at: string | null;
  altius_type?: string;
}

export interface DataItem {
  name: string;
  dealId: string | number | undefined;
  fetcherId: string | number | undefined;
  id: string | undefined;
  downloaded_at: string | null;
}

export interface DataroomFilesFilters {
  createdAtDate: { from: string | null; to: string | null } | null;
  syncAtDate: { from: string | null; to: string | null } | null;
  effectiveAtDate: { from: string | null; to: string | null } | null;
  uploadedAtDate: { from: string | null; to: string | null } | null;
  accounts: string[];
  deals: string[];
  docTypes: string[];
  altiusDeals: string[];
  dataroomsIds: null | number[];
  showLatest: boolean;
  synced: boolean;
  notSynced: boolean;
}

export interface ConnectionParams {
  type: string;
  username: string;
}

export type DownloadDataroomFilesType =
  | "sync"
  | "textract"
  | "K1-textract"
  | "management-fees-textract"
  | "ask-ai"
  | "file-data"
  | "classification"
  | "download";

export interface DataroomFileTable {
  columns: { title: string; key: string }[];
  rows: {
    name: string;
    accountName: string;
    docType: string;
    altiusClassification: string;
    created_at: string;
    dataroom: string;
    dealId?: number;
    dealName?: string;
    fetcherId: number;
    fundId: string;
    downloaded_at?: string | null;
    last_connected?: string | null;
    fundName: string;
    id: string | number;
  }[];
}

export interface DealDataromConnection {
  id: number;
  last_connected: string;
  logo: string;
  subdomain: string | null;
  type: string;
  username: string;
  isLoginValid: boolean;
}

export interface ConnectionStatus {
  id: number;
  type: string;
  username: string;
  subdomain: null | string;
  status: "syncing" | "idle" | "pending";
}

export enum RecommendationStatus {
  declined = "declined",
  accepted = "accepted",
  new = "new"
}

export interface IRecommendationDataFilters {
  deal_ids?: number[];
  investor_ids?: number[];
  measure_ids?: number[];
  effective_date?: { from: string; to: string };
  created_at?: { from: string; to: string };
  statuses?: string[];
  page?: number;
  per_page?: number;
}

export interface IRecommendationItem {
  id: number;
  currency: CurrencyType;
  value: number;
  measure: {
    id: number;
    name: string;
    is_global_value?: boolean;
  } | null;
  investor: {
    id: number;
    name: string;
  } | null;
  date: string;
  classification: string;
  status: RecommendationStatus;
  reference: string;
  polygon_id: string;
  children?: IRecommendationItem[];
}

export interface IMeasureGroupOneInvestor {
  currency: CurrencyType;
  value: number;
  measure: {
    id: number;
    name: string;
    is_global_value?: boolean;
  } | null;
  date: string;
  investor: {
    id: number;
    name: string;
  } | null;
  children: IRecommendationItem[];
  reference: string;
  classification: string;
}

export interface IMeasureGroupChild {
  value?: number | null;
  measure: {
    id: number;
    name: string;
    is_global_value?: boolean;
  } | null;
  currency: CurrencyType;
  date: string;
  classification: string;
  reference: string;
  children: (IMeasureGroupOneInvestor | IRecommendationItem)[];
}

export type IRecommendationChild = IMeasureGroupChild | IMeasureGroupOneInvestor | IRecommendationItem;

export interface IRecommendationData {
  account_id: number;
  total_count: number;
  deal: {
    id: number;
    title: string;
  };
  file: {
    id: number;
    name: string;
    url: string;
    page: number | null;
    document_id: number | null;
    section_id: number | null;
  };
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  children: IRecommendationChild[];
}

export interface PagedRecommendations {
  recommendations: IRecommendationData[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface IApproveMeasureFilters {
  file_id: number;
  measure_id: number;
  investor_id?: number;
  date?: string;
}

export interface IUpdateRecommendationStatusPayload {
  accepted_ids: number[];
  declined_ids: number[];
  new_ids: number[];
  filters?: IApproveMeasureFilters;
}

export * from "./api";
